<app-layout>
  <div class="success-container">
    <!-- Loading State -->
    @if (loading) {
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Carregando dados da sua compra...</p>
      </div>
    }

    <!-- Error State -->
    @if (error && !loading) {
      <div class="error-state">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h2>Ops! Algo deu errado</h2>
        <p>{{ error }}</p>
        <div class="error-actions">
          <app-button styleType="primary" (click)="loadPurchaseData()">Tentar Novamente</app-button>
          <app-button styleType="secondary" (click)="goToPedidos()">{{
            isSeller ? "Ver Meus Pedidos" : "Ver Meus Ingressos"
          }}</app-button>
        </div>
      </div>
    }

    <!-- Success State -->
    @if (order && !loading && !error) {
      <div class="success-content">
        <!-- Header por estado do pagamento: só é sucesso quando o pagamento foi aprovado -->
        @if (isPaid) {
          <div class="success-header">
            <div class="success-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h1>Compra realizada com sucesso!</h1>
            <p class="success-subtitle">Obrigado por escolher a Yes7 Tickets!</p>
          </div>
        } @else if (isPending) {
          <div class="success-header pending-header">
            <div class="success-icon">
              <i class="fas fa-clock"></i>
            </div>
            <h1>Estamos confirmando seu pagamento</h1>
            <p class="success-subtitle">
              Assim que o pagamento for confirmado, seus ingressos ficam disponíveis em "Meus Ingressos".
            </p>
          </div>
        } @else {
          <div class="success-header failed-header">
            <div class="success-icon">
              <i class="fas fa-times-circle"></i>
            </div>
            <h1>Pagamento não aprovado</h1>
            <p class="success-subtitle">
              Não foi possível concluir a compra e nenhum valor foi cobrado. Se aparecer um bloqueio no
              cartão, ele será liberado automaticamente pelo banco.
            </p>
          </div>
        }

        <!-- Order Summary Card -->
        <div class="order-summary-card">
          <div class="card-header">
            <h3>
              <i class="fas fa-receipt"></i>
              Resumo da Compra
            </h3>
            <span class="order-id">Pedido #{{ order.id }}</span>
          </div>

          <div class="order-details">
            <div class="detail-row">
              <span class="label">Data da compra:</span>
              <span class="value">{{ order.paid_at | date: "short" }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Método de pagamento:</span>
              <span class="value">{{ getPaymentMethodText(order.payment_method) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Status:</span>
              <span
                class="value"
                [class.status-success]="isPaid"
                [class.status-pending]="isPending"
                [class.status-failed]="isFailed"
                >{{ getPaymentStatusText(order.payment_status) }}</span
              >
            </div>
            @if (order.coupon) {
              <div class="detail-row">
                <span class="label">Cupom ({{ order.coupon.code }}):</span>
                <span class="value">- {{ order.coupon.discount_amount | currency: "BRL" }}</span>
              </div>
            }
            <div class="detail-row total-row">
              <span class="label">{{ isPaid ? "Total pago:" : "Total do pedido:" }}</span>
              <span class="value total-amount">{{ order.total_amount | currency: "BRL" }}</span>
            </div>
          </div>
        </div>

        <!-- Ingressos, detalhes do evento e próximos passos: só quando o pagamento foi aprovado -->
        @if (isPaid) {
        <!-- Event Details Card -->
        <div class="event-details-card">
          <div class="card-header">
            <h3>
              <i class="fas fa-calendar-alt"></i>
              Detalhes do Evento
            </h3>
          </div>

          <div class="event-info">
            <div class="event-thumbnail">
              @if (order.tickets[0]?.event_thumbnail) {
                <img
                  [src]="loadEventImage(order.tickets[0].event_thumbnail)"
                  [alt]="getEventTitle()"
                  appImgFallback="./../../../../assets/images/evento-sem-imagem.svg" />
              } @else {
                <div class="placeholder-thumbnail">
                  <i class="fas fa-image"></i>
                </div>
              }
            </div>

            <div class="event-details">
              <h4 class="event-title">{{ getEventTitle() }}</h4>

              <div class="event-meta">
                <div class="meta-item">
                  <i class="fas fa-calendar"></i>
                  <span>{{ getEventDate() | date: "shortDate" }}</span>
                </div>
                <div class="meta-item">
                  <i class="fas fa-clock"></i>
                  <span>{{ getEventTime() }}</span>
                </div>
                <div class="meta-item">
                  <i class="fas fa-map-marker-alt"></i>
                  <span>{{ getEventLocation() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tickets Summary -->
        <div class="tickets-summary-card">
          <div class="card-header">
            <h3>
              <i class="fas fa-ticket-alt"></i>
              Seus Ingressos
            </h3>
            <span class="ticket-count">{{ getTotalTickets() }} ingresso(s)</span>
          </div>

          <div class="tickets-list">
            @for (ticket of order.tickets; track ticket.id) {
              <div class="ticket-item">
                <div class="ticket-info">
                  <h5>{{ ticket.title }}</h5>
                  <p>{{ ticket.user_name }} {{ ticket.user_last_name }}</p>
                  <small>{{ ticket.user_email }}</small>
                </div>
                <div class="ticket-price">
                  {{ ticket.price | currency: "BRL" }}
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Next Steps -->
        <div class="next-steps-card">
          <div class="card-header">
            <h3>
              <i class="fas fa-info-circle"></i>
              Próximos Passos
            </h3>
          </div>

          <div class="steps-list">
            @if (isSeller) {
              <div class="step-item">
                <div class="step-icon">
                  <i class="fas fa-envelope"></i>
                </div>
                <div class="step-content">
                  <h5>Confirmação ao cliente</h5>
                  <p>O comprador recebe um e-mail com os detalhes da compra e dos ingressos.</p>
                </div>
              </div>

              <div class="step-item">
                <div class="step-icon">
                  <i class="fas fa-clipboard-list"></i>
                </div>
                <div class="step-content">
                  <h5>Acompanhe suas vendas</h5>
                  <p>Em "Meus pedidos" você consulta esta venda e outras dos eventos em que vende.</p>
                </div>
              </div>

              <div class="step-item">
                <div class="step-icon">
                  <i class="fas fa-qrcode"></i>
                </div>
                <div class="step-content">
                  <h5>Check-in no evento</h5>
                  <p>Os QR Codes dos ingressos ficam disponíveis para o organizador validar na entrada.</p>
                </div>
              </div>
            } @else {
              <div class="step-item">
                <div class="step-icon">
                  <i class="fas fa-envelope"></i>
                </div>
                <div class="step-content">
                  <h5>Confirmação por email</h5>
                  <p>Você receberá um email com todos os detalhes da sua compra</p>
                </div>
              </div>

              <div class="step-item">
                <div class="step-icon">
                  <i class="fas fa-qrcode"></i>
                </div>
                <div class="step-content">
                  <h5>QR Code dos ingressos</h5>
                  <p>Acesse "Meus Ingressos" para visualizar os QR codes</p>
                </div>
              </div>

              <div class="step-item">
                <div class="step-icon">
                  <i class="fas fa-mobile-alt"></i>
                </div>
                <div class="step-content">
                  <h5>Apresente no evento</h5>
                  <p>Leve seu celular com os QR codes para o evento</p>
                </div>
              </div>
            }
          </div>
        </div>
        }

        <!-- Action Buttons -->
        <div class="action-buttons">
          @if (isPaid) {
            <app-button styleType="primary" (click)="goToPedidos()">
              @if (isSeller) {
                <i class="fas fa-clipboard-list"></i>
                Ver Meus Pedidos
              } @else {
                <i class="fas fa-ticket-alt"></i>
                Ver Meus Ingressos
              }
            </app-button>
          } @else {
            <app-button styleType="primary" (click)="goToPedidos()">
              <i class="fas fa-clipboard-list"></i>
              Ver Meus Pedidos
            </app-button>
          }
          <app-button styleType="secondary" (click)="goToHome()">
            <i class="fas fa-home"></i>
            Voltar ao Início
          </app-button>
        </div>

        <!-- Security Notice -->
        @if (isPaid) {
          <div class="security-notice">
            <i class="fas fa-shield-alt"></i>
            <p>Seus dados estão protegidos com criptografia SSL. Guarde este número de pedido para referência.</p>
          </div>
        }
      </div>
    }
  </div>
</app-layout>
